<script setup>
import {computed, inject, onMounted, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import NavBar from '@/components/NavBar'
import {usePreviousConference} from '@/composables/usePreviousConference'
import PreviousConferenceHeader from '@/components/previous-conference/PreviousConferenceHeader'
import PreviousConferenceIntroSection from '@/components/previous-conference/PreviousConferenceIntroSection'
import PreviousConferenceAudienceStats from '@/components/previous-conference/PreviousConferenceAudienceStats'
import PreviousConferenceProgramStats from '@/components/previous-conference/PreviousConferenceProgramStats'
import PreviousConferencePhotoGallery from '@/components/previous-conference/PreviousConferencePhotoGallery'
import PreviousConferenceSpeakersSection from '@/components/previous-conference/PreviousConferenceSpeakersSection'
import PreviousConferenceSponsorsSection from '@/components/previous-conference/PreviousConferenceSponsorsSection'
import PreviousConferenceDownloads from '@/components/previous-conference/PreviousConferenceDownloads'

const contentData = inject('contentData')
const route = useRoute()
const router = useRouter()

const {
    previousConference,
    loading,
    error,
    fetchPreviousConferenceByYear,
} = usePreviousConference()

/**
 * Input example: route.params.year = "2024"
 * Output example: previousConference.value = { id: 3, date: "Personalized Health 2026", photos: [], videos: [], sponsors: [] }
 */
const loadPreviousConference = async () => {
    await fetchPreviousConferenceByYear(route.params.year)
}

const hasGalleryPhotos = computed(() => {
    const galleries = previousConference.value?.galleries || []
    return galleries.some(gallery => Array.isArray(gallery.photos) && gallery.photos.length > 0)
})

/**
 * Input example: route.params.year = "2024"
 * Output example: navigates to /previous-conferences/2024/galleries
 */
const openGalleryPage = () => {
    router.push({path: `/previous-conferences/${route.params.year}/galleries`})
}

onMounted(async () => {
    await loadPreviousConference()
})

watch(
    () => route.params.year,
    async () => {
        await loadPreviousConference()
    },
)
</script>

<template>
    <div class="min-h-screen bg-white">
        <NavBar />

        <PreviousConferenceHeader
            :background-image-path="contentData.background_image_path"
            :current-conference-label="previousConference?.date || 'Previous Conference'" />

        <section class="relative">
            <div class="container px-4 mx-auto py-6 sm:py-10">
                <div class="border-b border-slate-200 pb-3 sm:pb-4">
                    <div class="inline-flex border-b-2 border-teal-700 pb-3 text-sm sm:text-base font-semibold text-teal-700">
                        {{ previousConference?.date || 'Previous Conference' }}
                    </div>
                </div>

                <div class="py-8 sm:py-12">
                    <div v-if="loading" class="text-slate-700">
                        Loading previous conference...
                    </div>

                    <div v-else-if="error" class="text-red-700">
                        {{ error }}
                    </div>

                    <div v-else-if="previousConference" class="space-y-10 sm:space-y-14">
                        <PreviousConferenceIntroSection :previous-conference="previousConference" />

                        <div v-if="hasGalleryPhotos" class="flex justify-center">
                            <button
                                class="inline-flex items-center rounded-md bg-teal-700 px-8 py-4 text-base font-semibold text-white shadow-sm transition hover:bg-teal-800"
                                @click="openGalleryPage">
                                Foto Gallery
                            </button>
                        </div>

                        <PreviousConferenceAudienceStats :previous-conference="previousConference" />
                        <PreviousConferenceProgramStats :previous-conference="previousConference" />
                        <PreviousConferencePhotoGallery :previous-conference="previousConference" />
                        <PreviousConferenceSpeakersSection :previous-conference="previousConference" />
                        <PreviousConferenceSponsorsSection :previous-conference="previousConference" />
                        <PreviousConferenceDownloads :previous-conference="previousConference" />
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped></style>
